import { AddressLink } from 'Modules'

import { getContract } from 'hooks/contract/use-contract-viem'
import { clientWallet, publicWallet } from 'libs/viem/config'
import { logError, logInfo } from 'utils/logger'

/** StakingNFT.updateEndTime(nftAddress, newEndTime) - same signature on the MEY and USDC pools */
const ABI_STAKING_NFT_UPDATE_END_TIME = [
  {
    inputs: [
      { internalType: 'address', name: 'nftAddress', type: 'address' },
      { internalType: 'uint256', name: 'newEndTime', type: 'uint256' },
    ],
    name: 'updateEndTime',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
]

/** PTO ERC-3643 staking pool (one contract per PTO) - updateEndTime(uint64 _newEndTime) */
const ABI_STAKING_NFT_UPDATE_END_TIME_3643_STAKING = [
  {
    type: 'function',
    name: 'updateEndTime',
    inputs: [
      {
        name: '_newEndTime',
        type: 'uint64',
        internalType: 'uint64',
      },
    ],
    outputs: [],
    stateMutability: 'nonpayable',
  },
]

export interface StopStakingRewardsArgs {
  /**
   * staking pool contract: STAKE_NFT_ADDRESS (USDC) or STAKE_NFT_ADDRESS_MEY (MEY);
   * PTO ERC-3643: the PTO's own staking pool
   */
  poolAddress: string
  /** PTO NFT collection registered in the pool - not used for PTO ERC-3643 */
  collectionAddress?: string
  /** tokenize.sellingVersion === ERC3643 */
  isErc3643?: boolean
}

/**
 * System Actions (SRS 5.8/5.9) - stop staking rewards of one PTO collection by
 * capping its reward end time at now via StakingNFT.updateEndTime.
 */
export const useStopStakingRewards = () => {
  const stopStakingRewards = async (address: AddressLink, args: StopStakingRewardsArgs) => {
    if (!address) throw new Error('Wallet is not connected')
    if (!args.poolAddress) throw new Error('Staking pool contract is missing')
    if (!args.isErc3643 && !args.collectionAddress) {
      throw new Error('PTO collection contract is missing')
    }

    const contract = getContract({
      contractAddress: args.poolAddress as `0x${string}`,
      ABI: args.isErc3643
        ? ABI_STAKING_NFT_UPDATE_END_TIME_3643_STAKING
        : ABI_STAKING_NFT_UPDATE_END_TIME,
    })

    try {
      const nowSec = Math.floor(Date.now() / 1000)
      // ERC-3643 pool: end 20s from now
      const newEndTime = BigInt(args.isErc3643 ? nowSec + 20 : nowSec)
      logInfo('Stop staking rewards', { ...args, newEndTime })

      // ERC-3643 pool is per PTO, so no collection argument
      const { request } = await contract.simulate.updateEndTime(
        args.isErc3643 ? [newEndTime] : [args.collectionAddress as `0x${string}`, newEndTime],
        { account: address }
      )
      const txHash = await clientWallet?.writeContract(request)

      const receipt = await publicWallet.waitForTransactionReceipt({
        hash: txHash,
        confirmations: 5,
      })

      console.log({ receipt })

      return { txHash, receipt }
    } catch (error) {
      logError('error stop staking rewards on-chain-----------------', error)
      throw error
    }
  }

  return { stopStakingRewards }
}
